import React from "react";
import { Alert, AlertDescription, Box, Flex, Text, useDisclosure } from "@chakra-ui/react";
import { AppliedFocus } from "./AppliedFocus";
import { TabLimitPill } from "@/components/TabLimit/TabLimitPill";
import { EditingFocus } from "@/components/FocusLibraryDrawer/FocusPage";
import { envConfig } from "@/config";
import { ChakraFAIcon } from "@/components/ui/Icon";
import { SeatCardSizeSlider } from "@/components/SeatPlan/SeatCardSizeSlider";
import { useSeatPlanStore } from "@/stores/v2/seatPlanStore";
import { useUserSessionStore } from "@/stores/v2/userSessionStore";
import { useSelectedClassroomStore } from "@/stores/v2/selectedClassroomStore";
import { useTranslation } from "react-i18next";
import { useIsSavingSeatLayout, useResetSeatLayout, useSaveSeatLayout } from "@/components/SeatPlan/useSaveSeatLayout";
import { floatingToolbarBg } from "@/components/SeatPlan/FloatingToolbar";
import { sortSeatRosterIds } from "@/components/SeatPlan/seatLayout";
import { ConfirmResetAutoSortModal } from "@/components/SeatPlan/ConfirmResetAutoSortModal";
import { useToast } from "@/components/ui/Toast";

interface Props {
	studentCount: number;

	isUpdatingFocus: boolean;

	setTabItemLimit: (limit: number) => void;
	appliedFocuses: EditingFocus[];
	endFocus: (focusId: string) => void;
	classIsActive: boolean;

	bubbleUpdatedFocus: (focus: EditingFocus) => void;

	// Tab Limit props
	tabLimit?: number | null;
	onEditTabLimit?: () => void;
	onEndTabLimit?: () => void;
	isUpdatingTabLimit?: boolean;

	/**
	 * Qoria user ids in the classroom's live activity order, so a reset seats students in the same
	 * order the tiles are already in (see `sortSeatRosterIds`).
	 */
	activityOrderedIds?: string[];
}

export function FocusAndFlexibleGridBar(props: Props): JSX.Element {
	const { t } = useTranslation();
	const maxStudents = envConfig.MAX_STUDENTS;
	const layoutSortOrder = useSeatPlanStore((s) => s.layoutSortOrder);
	const hasUnsavedArrangement = useSeatPlanStore((s) => s.hasUnsavedArrangement);
	const saveArrangement = useSeatPlanStore((s) => s.saveArrangement);
	const discardArrangement = useSeatPlanStore((s) => s.discardArrangement);

	const teacherId = useUserSessionStore().getTeacherId();
	const classroomId = useSelectedClassroomStore((s) => s.stableIdV1);
	const students = useSelectedClassroomStore((s) => s.students);
	const saveSeatLayout = useSaveSeatLayout(teacherId, classroomId);
	const resetSeatLayout = useResetSeatLayout(teacherId, classroomId);
	const isSavingLayout = useIsSavingSeatLayout(teacherId, classroomId);
	const resetConfirm = useDisclosure();
	const { successToast } = useToast();

	const handleHeaderSave = () => {
		const layout = saveArrangement();
		saveSeatLayout.mutate(layout, {
			onSuccess: () => successToast({ title: t("seatPlan.toastSaved"), isClosable: true }),
			onError: (e) => console.warn("Failed to persist seat layout", e),
		});
	};

	const handleResetToAutoSort = () => {
		if (hasUnsavedArrangement) {
			discardArrangement();
		}
		const studentIds = sortSeatRosterIds(students ?? [], props.activityOrderedIds);
		useSeatPlanStore.getState().rearrangeStudents(studentIds, "lastNameAscending");
		resetSeatLayout.mutate(undefined, { onError: (e) => console.warn("Failed to reset seat layout", e) });
	};

	return (
		<Flex justifyContent={"space-between"} alignItems={"center"} mx={"24px"} mt={"16px"} mb={"4px"} data-seatplan-marquee-zone="">
			{props.studentCount > maxStudents && (
				<Flex>
					<Alert status="error" mb="16px" pt="12px" width="unset">
						<Box mx="8px" pt="-4px">
							<ChakraFAIcon icon="fa-triangle-exclamation" variant="solid" color="#B84C56" />
						</Box>
						<AlertDescription fontSize="14px" fontWeight="500" pt="2px">
							{t("classroomDetails.classCannotStartExceedsLimit", { studentCount: props.studentCount, maxStudents })}
						</AlertDescription>
					</Alert>
				</Flex>
			)}

			<Flex alignItems={"center"} gap="10px">
				{props.tabLimit !== null && props.tabLimit !== undefined && props.onEditTabLimit && props.onEndTabLimit && (
					<TabLimitPill
						tabLimit={props.tabLimit}
						classIsActive={props.classIsActive}
						onEdit={props.onEditTabLimit}
						onEnd={props.onEndTabLimit}
						isUpdating={props.isUpdatingTabLimit || false}
					/>
				)}
				{props.appliedFocuses.map((focus) => (
					<AppliedFocus
						key={focus.id}
						focus={focus}
						isUpdatingFocus={props.isUpdatingFocus}
						endFocus={props.endFocus}
						classIsActive={props.classIsActive}
						bubbleUpdatedFocus={props.bubbleUpdatedFocus}
					/>
				))}
			</Flex>
			<Flex>
				{props.appliedFocuses.length > 0 && !props.classIsActive && <Text>{t("classroomDetails.focusPresetStartsAutomatically")}</Text>}
			</Flex>
			<Flex alignItems={"center"} gap={3}>
				{props.studentCount > 0 && (
					<>
						{layoutSortOrder === "custom" && (
							<Flex
								as="button"
								type="button"
								data-testid="seatplan-reset-auto-sort-button"
								onClick={resetConfirm.onOpen}
								// The reset deletes the layout an in-flight save is still writing, so it waits
								// for that write to settle rather than racing it.
								disabled={isSavingLayout}
								height={"26px"}
								alignItems={"center"}
								px={3}
								gap={2}
								borderRadius={"md"}
								cursor={"pointer"}
								border="1px solid"
								borderColor={floatingToolbarBg}
								bg="white"
								color={floatingToolbarBg}
								transition={"opacity 0.15s ease"}
								_hover={{ opacity: 0.9 }}
								_active={{ opacity: 0.8 }}
								_disabled={{ opacity: 0.5, cursor: "not-allowed" }}
							>
								<ChakraFAIcon icon="fa-arrow-rotate-left" variant="solid" fontSize="13px" />
								<Text fontFamily="Roboto" fontWeight={500} fontSize="14px" lineHeight="1">
									{t("seatPlan.resetToAutoSort")}
								</Text>
							</Flex>
						)}
						<SeatCardSizeSlider setTabItemLimit={props.setTabItemLimit} />
						{hasUnsavedArrangement && (
							<>
								<Flex
									as="button"
									type="button"
									data-testid="seatplan-header-discard-button"
									onClick={discardArrangement}
									height={"26px"}
									alignItems={"center"}
									px={3}
									borderRadius={"md"}
									cursor={"pointer"}
									border="1px solid"
									borderColor={floatingToolbarBg}
									bg="white"
									color={floatingToolbarBg}
									transition={"opacity 0.15s ease"}
									_hover={{ opacity: 0.9 }}
									_active={{ opacity: 0.8 }}
								>
									<Text fontFamily="Roboto" fontWeight={500} fontSize="14px" lineHeight="1">
										{t("seatPlan.discard")}
									</Text>
								</Flex>
								<Flex
									as="button"
									type="button"
									data-testid="seatplan-header-save-button"
									onClick={handleHeaderSave}
									height={"26px"}
									alignItems={"center"}
									px={3}
									gap={2}
									borderRadius={"md"}
									cursor={"pointer"}
									border="1px solid #0000"
									bg={floatingToolbarBg}
									color="white"
									transition={"opacity 0.15s ease"}
									_hover={{ opacity: 0.9 }}
									_active={{ opacity: 0.8 }}
								>
									<ChakraFAIcon icon="fa-check" variant="solid" fontSize="13px" />
									<Text fontFamily="Roboto" fontWeight={500} fontSize="14px" lineHeight="1">
										{t("seatPlan.save")}
									</Text>
								</Flex>
							</>
						)}
					</>
				)}
			</Flex>
			<ConfirmResetAutoSortModal
				isOpen={resetConfirm.isOpen}
				onClose={resetConfirm.onClose}
				onConfirm={handleResetToAutoSort}
				isConfirmDisabled={isSavingLayout}
			/>
		</Flex>
	);
}
